import { useCallback, useEffect, useRef, useState } from 'react';

import { ORIENTATION } from '../../../constants.js';
import { Dimensions } from '../../../types/dimensions.js';
import { ChartLegendOrientation, LegendRatio } from '../../../types/legend.js';
import {
  CATEGORICAL_LEGEND_CLASS,
  MIN_ITEM_WIDTH,
} from '../../legend/categorical/constants.js';
import { RANGE_LEGEND_CLASS } from '../../legend/range/constants.js';
import {
  getLabelsHeightForRangeBottom,
  getMaxItemSizeForCategoricalHorizontal,
  getMaxLabelForRangeHorizontal,
  getSizeForCategoricaVertical,
} from '../utils/automatic-ratio.js';

interface AutomaticRatioParams {
  legendCandidateSize?: number;
  legendRef: (node: HTMLDivElement | null) => void;
  readyForAutoRatio: boolean;
  refreshAutoRatio: () => void;
}

/**
 * Hook responsible of determining the best ratio to apply to the chart in order
 * to fully display the content of the legend.
 * @param orientation -
 * @param ratio -
 * @param containerAvailableArea -
 */
export const useAutomaticRatio = (
  orientation: ChartLegendOrientation,
  ratio: LegendRatio,
  containerAvailableArea: Dimensions,
): AutomaticRatioParams => {
  const [readyForAutoRatio, setReadyForAutoRatio] = useState(false);
  const [idealLegendSize, setIdealLegendSize] = useState<number | undefined>(
    undefined,
  );
  const readyForAutoRatioRef = useRef(readyForAutoRatio);
  readyForAutoRatioRef.current = readyForAutoRatio;

  const resetAutoRatio = useCallback(() => {
    if (!readyForAutoRatioRef.current) {
      return;
    }
    setReadyForAutoRatio(false);
    setIdealLegendSize(undefined);
  }, []);

  useEffect(() => {
    resetAutoRatio();
  }, [orientation, resetAutoRatio]);

  const legendNodeRef = useRef<HTMLDivElement | null>(null);

  const legendRef = useCallback((node: HTMLDivElement | null) => {
    legendNodeRef.current = node;
  }, []);

  const { height: containerHeight } = containerAvailableArea;
  const hasAutoRatioEnabled = !ratio || ratio === 'auto';

  useEffect(() => {
    if (!hasAutoRatioEnabled) {
      // oxlint-disable-next-line react/set-state-in-effect
      setReadyForAutoRatio(true);
      return;
    }

    const node = legendNodeRef.current;

    if (!node || containerHeight === 0 || readyForAutoRatio) {
      return;
    }

    let longestSeries;

    const isCategoricalLegend = Array.from(node.classList).includes(
      CATEGORICAL_LEGEND_CLASS,
    );
    const isRangeLegend = Array.from(node.classList).includes(
      RANGE_LEGEND_CLASS,
    );

    if (isCategoricalLegend) {
      longestSeries =
        orientation === ORIENTATION.Horizontal
          ? getMaxItemSizeForCategoricalHorizontal(node, containerHeight)
          : getSizeForCategoricaVertical();
    }
    if (isRangeLegend) {
      const hasLoadedLabels = node.getElementsByTagName('g').length !== 0;
      if (!hasLoadedLabels) {
        return;
      }

      longestSeries =
        orientation === ORIENTATION.Horizontal
          ? getMaxLabelForRangeHorizontal(node)
          : getLabelsHeightForRangeBottom(node);
    }

    setIdealLegendSize(
      longestSeries !== undefined && longestSeries > 0
        ? longestSeries
        : MIN_ITEM_WIDTH,
    );
    setReadyForAutoRatio(true);
  }, [containerHeight, readyForAutoRatio, hasAutoRatioEnabled, orientation]);

  return {
    legendCandidateSize: idealLegendSize,
    legendRef,
    readyForAutoRatio,
    refreshAutoRatio: resetAutoRatio,
  };
};
